import React, { useState, useEffect, useCallback } from 'react';
import CategoryGrid from './CategoryGrid';
import CategoryModal from './CategoryModal';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import ViewToolbar from '../../common/ViewToolbar';
import { useModal } from '../../../hooks/useModal';
import { useDebounce } from '../../../hooks/useDebounce';
import * as categoryService from '../../../services/categoryService';

/**
 * Self-contained Categories feature view.
 * Same pattern as CustomersView — owns its data, modals, and CRUD.
 */
export default function CategoriesView() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [gridApi, setGridApi] = useState(null);

  const editModal = useModal();
  const deleteModal = useModal();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setCategories(await categoryService.fetchCategories(debouncedSearchTerm));
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      if (editModal.record) {
        await categoryService.updateCategory(editModal.record.category_id, formData);
      } else {
        await categoryService.createCategory(formData);
      }
      editModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await categoryService.deleteCategory(deleteModal.record.category_id);
      deleteModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  return (
    <>
      <ViewToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => editModal.open(null)}
        onExportCsv={() => gridApi?.exportToCsv()}
        placeholder="Search categories..."
      />
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex justify-center mt-10">Loading...</div>
        ) : (
          <CategoryGrid
            rowData={categories}
            onEdit={(r) => editModal.open(r)}
            onDelete={(r) => deleteModal.open(r)}
            onGridReady={setGridApi}
          />
        )}
      </div>
      <CategoryModal
        isOpen={editModal.isOpen}
        onClose={editModal.close}
        onSubmit={handleSave}
        initialData={editModal.record}
        title={editModal.record ? 'Edit Category' : 'Add New Category'}
      />
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        recordName={deleteModal.record?.category_name || 'this record'}
      />
    </>
  );
}
